/**
 * The bottom band: where the round is, what is coming, and the button that
 * says "send it now".
 *
 * The composition preview shows each incoming archetype as its own terminal
 * glyph in its phosphor colour — the same mark the player will read on the
 * playfield a second later. Icons frame the strip; the glyphs inside it are
 * game art, not iconography.
 */

import { WAVES } from '@/core/config';
import type { PhosphorName } from '@/core/palette';
import { roman } from './ActPanel';
import { trackBandHeight } from './Bands';
import { el, groupDigits, icon, iconStack, installUiTheme, money, phosphorVar } from './Icon';

export interface WaveComposition {
  /**
   * Sprite-atlas key for the archetype (`grunt`, `armoured`). Doubles as the
   * family key the chips merge on. It is *not* a character — rendering it
   * verbatim turned the strip into `armoured ×18 grunt ×60 weaver ×60`.
   */
  glyph: string;
  /**
   * One character to print in the chip. When the enemy catalog grows a real
   * `mark` field this carries it; until then the initial stands in.
   */
  mark?: string;
  colour: PhosphorName;
  count: number;
  name: string;
}

/**
 * Where the run is inside the act it is playing.
 *
 * The HUD counted `⚑ 42 /100` — the run — and nothing counted the act, so the
 * intermission that demands a permanent choice arrived with no lead-up at all.
 * A player could not tell round 2 of twelve from round 12 of twelve, which
 * means they could not tell "keep building" from "this one ends it".
 *
 * Injected, like every other fact in this file. `game/Levels.ts` owns the
 * schedule; the bar owns none of it and imports none of it.
 */
export interface ActView {
  /** 1-based, as the fiction numbers them. */
  index: number;
  name: string;
  /** 1-based round *within* the act. */
  round: number;
  length: number;
  /** True on the last round of the act — the one the intermission follows. */
  finale: boolean;
  /** What clearing this act opens. Null on the last act in the run. */
  next: {
    name: string;
    /** Display names of the towers the next act unlocks. */
    towers: readonly string[];
  } | null;
}

export interface WaveBarState {
  round: number;
  totalRounds: number;
  /** Act progress. Null only before the session has a round. */
  act?: ActView | null;
  /** True between rounds, when the early-start control is live. */
  prep: boolean;
  /** 0..1. Spawn progress during combat, countdown progress during prep. */
  progress: number;
  /** Seconds left of the prep window. Drives the early-start bonus. */
  prepRemaining: number;
  incoming: readonly WaveComposition[];
  /** Name of the boss arriving this round, if any. */
  boss: string | null;
  canStartEarly: boolean;
  /** Cash the player keeps by starting now. */
  earlyBonus: number;
}

export interface WaveBarCallbacks {
  onStartEarly?(): void;
}

/** Chips beyond this crowd the band; the overflow collapses into a counter. */
const MAX_CHIPS = 6;

export class WaveBar {
  private root = el('div', 'hud-band hud-band--bottom');
  private bar = el('div', 'wavebar');
  private roundBox = el('div', 'wavebar__round');
  private roundNum = el('b', 'num', '0');
  private roundTotal = el('span', 'num', `/${WAVES.count}`);
  private track = el('div', 'wavebar__track');
  private fill = el('div', 'wavebar__fill');
  private comp = el('div', 'wavebar__comp');
  private bossTag = el('div', 'wavebar__boss');
  /**
   * The full name, and a four-character stand-in for it. Under 560px the wave
   * bar cannot spend 155px on `BOSS.SINGULARITY` and still fit SEND inside the
   * glass, so `ui.css` swaps which of the two is showing. Both stay in the DOM
   * because the tag's `aria-label` carries the full name either way.
   */
  private bossName = el('span', 'wavebar__boss-name', 'BOSS');
  private bossShort = el('span', 'wavebar__boss-short', 'BOSS');
  /*
   * The act cluster: which act, how far through it, and — on the last round —
   * what clearing it opens. One group, so the three facts read as one sentence
   * and so the whole thing can leave the band in one rule when a 320px phone
   * cannot afford it.
   */
  private actBox = el('div', 'wavebar__act');
  private actLabel = el('span', 'wavebar__act-label');
  private actName = el('span', 'wavebar__act-name');
  private actTrack = el('div', 'wavebar__act-track');
  private actFill = el('div', 'wavebar__act-fill');
  /** Last-written values, so a per-frame update() only touches the DOM on change. */
  private lastRound = '';
  private lastFill = '';
  private lastActFill = '';
  private lastBoss: string | null = null;
  private lastCells = 0;
  private lastBonus = -1;
  private lastStartRound = -1;
  private actCount = el('span', 'wavebar__act-n num', '');
  private nextTag = el('span', 'wavebar__next');
  private nextName = el('span', undefined, '');
  private startBtn = el('button', 'btn btn--primary wavebar__early');
  private startBonus = el('span', 'bonus num', '');
  private chips: HTMLElement[] = [];
  private untrack: (() => void) | null = null;
  private mounted = false;

  constructor(private readonly cb: WaveBarCallbacks = {}) {}

  mount(root: HTMLElement): void {
    if (this.mounted) return;
    this.mounted = true;
    installUiTheme();

    this.roundBox.append(
      icon('flag-checkered', { size: 16, role: 'uiWave', glow: 0.8, title: 'Round' }),
      this.roundNum,
      this.roundTotal,
    );

    this.track.append(this.fill, el('div', 'wavebar__ticks'));
    this.track.setAttribute('role', 'progressbar');
    this.track.setAttribute('aria-valuemin', '0');
    this.track.setAttribute('aria-valuemax', '100');
    this.track.setAttribute('aria-label', 'Round progress');

    // No single icon says "a boss is coming", so two real ones say it together:
    // the warning triangle badged with the crown the boss family already uses.
    this.bossTag.append(
      iconStack('exclamation-triangle', 'crown', {
        size: 16,
        badgeSize: 12,
        role: 'uiDanger',
        badgeRole: 'uiDanger',
        glow: 1.0,
      }),
      this.bossShort,
      this.bossName,
    );
    this.bossTag.hidden = true;

    this.startBtn.type = 'button';
    this.startBtn.append(
      icon('flag', { size: 12 }),
      el('span', undefined, 'Send'),
      this.startBonus,
    );
    this.startBtn.dataset.tip = 'Start the next round early and keep the unspent timer as cash. [Enter]';
    this.startBtn.addEventListener('click', this.onStart);

    /*
     * `sitemap` is the act mark: the corridor branching, which is the one thing
     * an act actually changes. It is the same vendored set the rest of the
     * cabinet draws from, at the same 12px the round pill's flag uses.
     */
    this.actTrack.append(this.actFill, el('div', 'wavebar__act-ticks'));
    this.actTrack.setAttribute('role', 'progressbar');
    this.actTrack.setAttribute('aria-valuemin', '0');
    this.actTrack.setAttribute('aria-valuemax', '100');
    this.actBox.append(
      icon('sitemap', { size: 12, role: 'uiAccent', glow: 0.6 }),
      this.actLabel,
      this.actName,
      this.actTrack,
      this.actCount,
    );
    this.actBox.hidden = true;

    // Only up on the act's last round, and only where the band can spare it.
    // The tooltip and the toast carry the same sentence everywhere else.
    this.nextTag.append(icon('angle-right', { size: 12, role: 'uiCash' }), this.nextName);
    this.nextTag.hidden = true;

    this.bar.append(
      this.roundBox, this.actBox, this.track, this.comp,
      this.nextTag, this.bossTag, this.startBtn,
    );
    this.root.append(this.bar);
    root.append(this.root);
    // The deck above stops where this band starts, and this band grows by a
    // 46px SEND button between rounds.
    this.untrack = trackBandHeight(this.root, '--wave-h');
  }

  update(state: WaveBarState): void {
    if (!this.mounted) return;

    // update() runs every frame from refreshUi. Writing an identical string to
    // textContent still replaces the text node, and setAttribute always dirties
    // the element — so every write below is guarded by a last-written cache.
    const roundText = groupDigits(state.round);
    if (roundText !== this.lastRound) {
      this.lastRound = roundText;
      this.roundNum.textContent = roundText;
      this.roundTotal.textContent = `/${state.totalRounds}`;
    }

    const pct = Math.max(0, Math.min(1, state.progress)) * 100;
    const fillScale = `scaleX(${(pct / 100).toFixed(3)})`;
    if (fillScale !== this.lastFill) {
      this.lastFill = fillScale;
      this.fill.style.transform = fillScale;
      this.track.setAttribute('aria-valuenow', pct.toFixed(0));
    }
    this.track.classList.toggle('is-prep', state.prep);
    this.track.classList.toggle('is-boss', state.boss !== null);

    this.bossTag.hidden = state.boss === null;
    if (state.boss && state.boss !== this.lastBoss) {
      this.lastBoss = state.boss;
      const name = state.boss.toUpperCase();
      this.bossName.textContent = name;
      this.bossTag.dataset.tip = `${name} arrives this round.`;
      this.bossTag.setAttribute('aria-label', `Boss incoming: ${name}`);
    }
    if (state.boss === null) this.lastBoss = null;

    this.renderAct(state.act ?? null);
    this.renderComposition(state.incoming);

    // The ticks used to fall every 9px regardless of the round, which made them
    // decoration. One cell per incoming family turns the track into a count:
    // "three groups left" is readable without touching the chips.
    const cells = Math.max(1, Math.min(12, this.merged.length));
    if (cells !== this.lastCells) {
      this.lastCells = cells;
      this.track.style.setProperty('--wave-groups', String(cells));
    }

    this.startBtn.hidden = !state.canStartEarly;
    // Rebuilt only when the inputs move; this ran two template strings and a
    // setAttribute per frame, hidden or not.
    if (state.earlyBonus !== this.lastBonus || state.round !== this.lastStartRound) {
      this.lastBonus = state.earlyBonus;
      this.lastStartRound = state.round;
      this.startBonus.textContent = state.earlyBonus > 0 ? `+${money(state.earlyBonus)}` : '';
      this.startBtn.setAttribute(
        'aria-label',
        state.earlyBonus > 0
          ? `Start round ${state.round + 1} early for ${state.earlyBonus} bonus cash`
          : `Start round ${state.round + 1} early`,
      );
    }
  }

  destroy(): void {
    this.startBtn.removeEventListener('click', this.onStart);
    this.untrack?.();
    this.untrack = null;
    this.chips.length = 0;
    this.root.remove();
    this.mounted = false;
  }

  /**
   * Chips are rebuilt only when the round's composition actually changes, which
   * is once per round rather than once per frame.
   */
  private lastSignature = '';

  /** Act, round-in-act and finale state, so the cluster repaints once a round. */
  private lastAct = '';

  /**
   * Reused across rounds so the merge allocates nothing per frame. A round has
   * a handful of families, never enough to want a Map here.
   */
  private readonly merged: { glyph: string; mark?: string; colour: WaveComposition['colour']; count: number; name: string }[] = [];
  /** How many slots of `merged` are live this frame; the rest are pooled. */
  private mergedLive = 0;

  /**
   * Two groups of grunts in one round are one fact about that round, not two.
   * The round table emits a group per spawn wave, so merging on the atlas key
   * is what turns `grunt ×60  grunt ×30` back into `G ×90`.
   *
   * The slots are pooled and overwritten in place — this runs every frame, and
   * spreading a fresh object per family was one allocation per family per
   * frame for a strip that changes once a round.
   */
  private mergeByFamily(incoming: readonly WaveComposition[]): readonly WaveComposition[] {
    this.mergedLive = 0;
    for (const group of incoming) {
      let hit: (typeof this.merged)[number] | undefined;
      for (let i = 0; i < this.mergedLive; i++) {
        if (this.merged[i].glyph === group.glyph) { hit = this.merged[i]; break; }
      }
      if (hit) {
        hit.count += group.count;
        continue;
      }
      const slot = this.merged[this.mergedLive] ?? (this.merged[this.mergedLive] = {
        glyph: '', colour: group.colour, count: 0, name: '',
      });
      slot.glyph = group.glyph;
      slot.mark = group.mark;
      slot.colour = group.colour;
      slot.count = group.count;
      slot.name = group.name;
      this.mergedLive++;
    }
    this.merged.length = this.mergedLive;
    return this.merged;
  }

  /**
   * The act readout, rebuilt only when the act or the round inside it moves.
   *
   * The bar fills by whole rounds including the one being played, so it is
   * never empty and it reads full exactly when the finale is on the board. A
   * fill that only counted *finished* rounds would say 11/12 on the round that
   * ends the act, which is the one round it has to be unambiguous about.
   */
  private renderAct(act: ActView | null): void {
    this.actBox.hidden = act === null;
    this.nextTag.hidden = act === null || !act.finale || act.next === null;
    if (!act) return;

    const signature = `${act.index}:${act.round}:${act.finale ? 1 : 0}`;
    if (signature === this.lastAct) return;
    this.lastAct = signature;

    this.actLabel.textContent = `Act ${roman(act.index)}`;
    this.actName.textContent = act.finale ? 'Final round' : act.name;
    this.actCount.textContent = `${act.round}/${act.length}`;
    this.actBox.classList.toggle('is-finale', act.finale);

    const pct = (act.round / Math.max(1, act.length)) * 100;
    const actScale = `scaleX(${(pct / 100).toFixed(3)})`;
    if (actScale !== this.lastActFill) {
      this.lastActFill = actScale;
      this.actFill.style.transform = actScale;
    }
    this.actTrack.style.setProperty('--act-rounds', String(Math.max(1, act.length)));
    this.actTrack.setAttribute('aria-valuenow', pct.toFixed(0));
    this.actTrack.setAttribute(
      'aria-label',
      `Act ${roman(act.index)}, ${act.name}: round ${act.round} of ${act.length}`,
    );

    /*
     * What is coming, in words, before the modal that asks about it. The
     * intermission already lists the next act's corridor, unlocks and refund —
     * this is the same sentence a round early, which is the difference between
     * a reward and an interruption.
     */
    const next = act.next;
    const opens = next === null
      ? 'Clearing it ends the run.'
      : `Clearing it opens ${next.name}: a new corridor, a choice of two abilities`
        + `${next.towers.length > 0 ? `, and ${next.towers.join(' and ')}` : ''}.`;
    this.actBox.dataset.tip = act.finale
      ? `Act ${roman(act.index)} — ${act.name} — last round. ${opens}`
      : `Act ${roman(act.index)} — ${act.name}. Round ${act.round} of ${act.length}. ${opens}`;

    if (next) {
      this.nextName.textContent = next.name.toUpperCase();
      this.nextTag.dataset.tip = opens;
      this.nextTag.setAttribute('aria-label', opens);
    }
  }

  private renderComposition(incoming: readonly WaveComposition[]): void {
    const groups = this.mergeByFamily(incoming);

    let signature = '';
    for (const group of groups) signature += `${group.glyph}${group.count},`;
    if (signature === this.lastSignature) return;
    this.lastSignature = signature;

    this.comp.replaceChildren();
    this.chips.length = 0;

    const shown = groups.slice(0, MAX_CHIPS);
    for (const group of shown) {
      const chip = el('span', 'comp-chip');
      chip.style.setProperty('--chip-col', phosphorVar(group.colour));
      chip.append(
        el('span', 'comp-chip__glyph', mark(group)),
        el('span', 'comp-chip__n num', `×${groupDigits(group.count)}`),
      );
      chip.dataset.tip = `${group.name} ×${group.count}`;
      chip.setAttribute('aria-label', `${group.name}, ${group.count} incoming`);
      this.comp.append(chip);
      this.chips.push(chip);
    }

    if (groups.length > MAX_CHIPS) {
      const rest = groups.length - MAX_CHIPS;
      const more = el('span', 'comp-chip');
      more.append(
        icon('ellipses-horizontal', { size: 12, role: 'uiText' }),
        el('span', 'comp-chip__n num', `+${rest}`),
      );
      more.dataset.tip = `${rest} more group${rest === 1 ? '' : 's'} this round`;
      this.comp.append(more);
    }
  }

  private readonly onStart = (): void => {
    this.cb.onStartEarly?.();
  };
}

/** One capital letter, so the chip stays a mark rather than becoming a word. */
function mark(group: WaveComposition): string {
  const source = group.mark ?? group.glyph;
  return source.charAt(0).toUpperCase();
}
